import { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Brand } from "../components/Shell";
import { Phone, Lock, User, Loader2, Heart, ChevronLeft, KeyRound, MessageCircle } from "lucide-react";

export default function Auth() {
  const { user, applyAuth } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", phone: "", password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("auth");

  if (user) return <Navigate to={user.is_admin ? "/admin" : user.profile_complete ? "/" : "/profile"} replace />;
  if (view === "forgot") return <ForgotFlow onBack={() => setView("auth")} />;

  const submit = async (e) => {
    e.preventDefault();
    const phoneDigits = form.phone.replace(/\D/g, "");
    if (phoneDigits.length !== 10 || !"6789".includes(phoneDigits[0])) {
      toast.error("Enter a valid 10-digit mobile number");
      return;
    }
    if (mode === "signup" && form.password !== form.confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login"
        ? { phone: phoneDigits, password: form.password }
        : { name: form.name, phone: phoneDigits, password: form.password };
      const { data } = await api.post(path, payload);
      applyAuth(data);
      toast.success(mode === "login" ? "Welcome back!" : "Account created!");
      const u = data.user;
      navigate(u.is_admin ? "/admin" : u.profile_complete ? "/" : "/profile", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="flex-1 overflow-y-auto no-scrollbar relative">
      <div className="absolute inset-0 pointer-events-none opacity-60" style={{ background: "radial-gradient(circle at 50% -10%, rgba(255,46,99,0.28), transparent 55%)" }} />
      <div className="relative px-7 pt-14 pb-8 min-h-full flex flex-col">
        <div className="flex flex-col items-center text-center gap-3 mb-10">
          <Brand size="lg" />
          <div data-testid="free-earning-text" className="px-4 py-1 rounded-full bg-rose-500/15 border border-rose-400/30">
            <span className="text-base font-extrabold tracking-wide bg-gradient-to-r from-[#FF2E63] to-[#FFB703] bg-clip-text text-transparent" style={{ fontFamily: "Outfit, sans-serif" }}>
              Free Earning
            </span>
          </div>
          <p className="text-slate-400 text-sm max-w-[16rem]">
            Find your match in <span className="text-rose-400 font-semibold">Kalaburagi</span> — real people, real sparks.
          </p>
        </div>

        <div className="flex p-1 rounded-2xl bg-white/5 border border-white/10 mb-7">
          {["login", "signup"].map((m) => (
            <button
              key={m}
              data-testid={m === "signup" ? "auth-signup-toggle-button" : "auth-login-toggle-button"}
              onClick={() => setMode(m)}
              className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${mode === m ? "bg-gradient-to-r from-rose-500 to-rose-600 text-white shadow-lg shadow-rose-500/25" : "text-slate-400"}`}
            >
              {m === "login" ? "Sign In" : "Create Account"}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="flex flex-col gap-3.5">
          {mode === "signup" && (
            <Field icon={User} testid="auth-name-input" placeholder="Your name"
              value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          )}
          <div className="relative" data-testid="auth-phone-field">
            <div className="absolute left-0 top-0 bottom-0 flex items-center gap-2 pl-4 pr-3 pointer-events-none">
              <Phone className="w-5 h-5 text-slate-500" />
              <span className="text-white text-sm font-semibold border-r border-white/15 pr-3">+91</span>
            </div>
            <input
              data-testid="auth-phone-input"
              type="tel"
              inputMode="numeric"
              maxLength={10}
              placeholder="Mobile number"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
              required
              className="w-full h-12 pl-[6.25rem] pr-4 rounded-2xl bg-white/5 border border-white/10 text-white placeholder:text-slate-500 text-sm focus:outline-none focus:border-rose-500/60 focus:ring-2 focus:ring-rose-500/20 transition-all"
            />
          </div>
          <Field icon={Lock} testid="auth-password-input" type="password" placeholder="Password"
            value={form.password} onChange={(v) => setForm({ ...form, password: v })} />
          {mode === "signup" && (
            <Field icon={Lock} testid="auth-confirm-input" type="password" placeholder="Confirm password"
              value={form.confirm} onChange={(v) => setForm({ ...form, confirm: v })} />
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="auth-login-button"
            className="mt-2 h-12 rounded-2xl bg-gradient-to-r from-[#FF2E63] to-[#FF6B8B] text-white font-bold text-base flex items-center justify-center gap-2 shadow-lg shadow-rose-500/30 active:scale-[0.98] transition-transform disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Heart className="w-5 h-5" fill="currentColor" />}
            {mode === "login" ? "Sign In" : "Create Account"}
          </button>
          {mode === "login" && (
            <button type="button" data-testid="forgot-password-link" onClick={() => setView("forgot")}
              className="text-sm text-rose-400 font-semibold mx-auto mt-1 active:opacity-70">
              Forgot password?
            </button>
          )}
        </form>

        <div className="flex items-center gap-3 my-6">
          <div className="h-px flex-1 bg-white/10" />
          <span className="text-xs text-slate-500 font-medium">or continue with</span>
          <div className="h-px flex-1 bg-white/10" />
        </div>

        <button
          onClick={googleLogin}
          data-testid="auth-google-button"
          className="h-12 rounded-2xl bg-white/5 border border-white/15 text-white font-semibold flex items-center justify-center gap-3 active:scale-[0.98] transition-transform hover:bg-white/10"
        >
          <GoogleIcon />
          Continue with Google
        </button>

        <p className="text-center text-xs text-slate-500 mt-auto pt-8">
          By continuing you agree to GulMatch's community guidelines.
        </p>
      </div>
    </div>
  );
}

function Field({ icon: Icon, testid, type = "text", placeholder, value, onChange }) {
  return (
    <div className="relative">
      <Icon className="w-5 h-5 text-slate-500 absolute left-4 top-1/2 -translate-y-1/2" />
      <input
        data-testid={testid}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        className="w-full h-12 pl-12 pr-4 rounded-2xl bg-white/5 border border-white/10 text-white placeholder:text-slate-500 text-sm focus:outline-none focus:border-rose-500/60 focus:ring-2 focus:ring-rose-500/20 transition-all"
      />
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
    </svg>
  );
}

/* ---------------- Forgot password (WhatsApp reset link) ---------------- */
function ForgotFlow({ onBack }) {
  const [step, setStep] = useState("phone"); // phone | sent
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const validPhone = phone.length === 10 && "6789".includes(phone[0]);

  const sendLink = async () => {
    if (!validPhone) { setErr("Enter a valid 10-digit mobile number"); return; }
    setErr(""); setLoading(true);
    try {
      await api.post("/auth/forgot-password", { phone });
      setStep("sent"); setCooldown(600);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="flex-1 overflow-y-auto no-scrollbar relative" data-testid="forgot-password-page">
      <div className="absolute inset-0 pointer-events-none opacity-60" style={{ background: "radial-gradient(circle at 50% -10%, rgba(255,46,99,0.28), transparent 55%)" }} />
      <div className="relative px-7 pt-8 pb-8 min-h-full flex flex-col">
        <button data-testid="forgot-back-button" onClick={() => (step === "sent" ? (setErr(""), setStep("phone")) : onBack())}
          className="w-10 h-10 rounded-full bg-white/5 border border-white/10 grid place-items-center text-slate-300 active:scale-90 transition-transform mb-6">
          <ChevronLeft className="w-5 h-5" />
        </button>

        <div className="flex flex-col items-center text-center gap-3 mb-8">
          <Brand size="lg" />
          <div className="w-12 h-12 rounded-2xl bg-rose-500/15 grid place-items-center">
            {step === "sent" ? <MessageCircle className="w-6 h-6 text-emerald-400" /> : <KeyRound className="w-6 h-6 text-rose-400" />}
          </div>
          <h1 className="text-2xl font-extrabold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
            {step === "sent" ? "Request received" : "Forgot password?"}
          </h1>
          <p className="text-slate-400 text-sm max-w-[18rem]">
            {step === "sent"
              ? `If +91 ${phone} is registered, our team will send a secure password-reset link to its WhatsApp shortly.`
              : "Enter your registered mobile number. Our team will send a secure reset link to its WhatsApp."}
          </p>
        </div>

        {err && <p data-testid="forgot-error" className="text-sm text-rose-400 text-center mb-4">{err}</p>}

        {step === "phone" && (
          <div className="flex flex-col gap-3.5">
            <div className="relative" data-testid="forgot-phone-field">
              <div className="absolute left-0 top-0 bottom-0 flex items-center gap-2 pl-4 pr-3 pointer-events-none">
                <Phone className="w-5 h-5 text-slate-500" />
                <span className="text-white text-sm font-semibold border-r border-white/15 pr-3">+91</span>
              </div>
              <input data-testid="forgot-phone-input" type="tel" inputMode="numeric" maxLength={10}
                placeholder="Registered mobile number" value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                className="w-full h-12 pl-[6.25rem] pr-4 rounded-2xl bg-white/5 border border-white/10 text-white placeholder:text-slate-500 text-sm focus:outline-none focus:border-rose-500/60 focus:ring-2 focus:ring-rose-500/20 transition-all" />
            </div>
            <button data-testid="forgot-send-link" onClick={sendLink} disabled={loading}
              className="mt-2 h-12 rounded-2xl bg-gradient-to-r from-[#FF2E63] to-[#FF6B8B] text-white font-bold flex items-center justify-center gap-2 shadow-lg shadow-rose-500/30 active:scale-[0.98] transition-transform disabled:opacity-60">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <MessageCircle className="w-5 h-5" />}
              Request reset link
            </button>
          </div>
        )}

        {step === "sent" && (
          <div className="flex flex-col gap-3.5">
            <div className="p-4 rounded-2xl bg-white/5 border border-white/10 flex items-start gap-3" data-testid="forgot-request-sent">
              <MessageCircle className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
              <p className="text-[13px] text-slate-300 leading-relaxed">Keep an eye on WhatsApp — the reset link works once and expires 30 minutes after it's generated.</p>
            </div>
            <button data-testid="forgot-resend-link" onClick={sendLink} disabled={loading || cooldown > 0}
              className="text-sm text-rose-400 font-semibold mx-auto active:opacity-70 disabled:opacity-40 disabled:text-slate-500">
              {loading ? "Submitting…" : cooldown > 0
                ? `Request again in ${Math.floor(cooldown / 60)}:${String(cooldown % 60).padStart(2, "0")}`
                : "Request again"}
            </button>
            <button data-testid="forgot-back-to-login" onClick={onBack}
              className="text-sm text-slate-400 font-semibold mx-auto active:opacity-70">
              Back to Sign In
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
